import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Command } from 'cmdk'
import {
  IconMagnifyingGlass,
  EmptyState,
  GreyGrey20,
  GreyGrey30,
  GreyGrey60,
  GreyGrey80,
  ShadowL,
  Modal,
  Spinner,
  Box,
  Flex
} from '@sequencehq/core-components'
import {
  useGlobalSearchKeyBinding,
  useGlobalSearchState
} from './useGlobalSearchShortcut'
import { useGlobalSearchResults } from './useGlobalSearchResults'
import { CustomerResults } from './sections/CustomerResults'
import { InvoiceResults } from './sections/InvoiceResults'
import { IconSlot } from './components/IconSlot'
import {
  CUSTOMER_MIN_CHARS,
  SEARCH_PLACEHOLDER
} from './globalSearch.constants'

/**
 * Command palette (⌘K / Ctrl+K) for quickly finding customers and invoices.
 * Mounted once at the app layout level. Always rendered so Chakra can animate
 * the exit before unmounting content.
 */
export const GlobalSearchPalette = () => {
  useGlobalSearchKeyBinding()
  const { isOpen, setIsOpen } = useGlobalSearchState()
  const [query, setQuery] = useState('')
  const navigate = useNavigate()
  const results = useGlobalSearchResults(query)

  const close = () => {
    setIsOpen(false)
    setQuery('')
  }

  const onNavigate = (path: string) => {
    navigate(path)
    close()
  }

  const anyLoading = results.isCustomersLoading || results.isInvoicesLoading
  const hasResults = results.customers.length > 0 || results.invoices.length > 0
  const showNoResults = results.isActive && !anyLoading && !hasResults
  const showInitialHint = results.query.length < CUSTOMER_MIN_CHARS
  const showEmptyState = showInitialHint || showNoResults
  const emptyStateText = showInitialHint
    ? SEARCH_PLACEHOLDER
    : `No results for "${results.query}"`

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      width="380px"
      motionPreset="scale"
      overlayBackground="rgba(0,0,0,0.1)"
      overlayBlur
      boxShadow={`0 0 0 1px rgba(0,0,0,0.12), ${ShadowL}`}
      // Higher up on a phone, where the on-screen keyboard takes the bottom
      // half of the screen.
      top={{ base: '72px', md: '200px' }}
      data-testid="global-search"
    >
      <Box sx={cmdkStyles}>
        <Command shouldFilter={false} label="Global search">
          <Flex
            h="48px"
            align="center"
            gap="12px"
            px="12px"
            borderBottom={`1px solid ${GreyGrey30}`}
            bg="white"
            flexShrink={0}
          >
            <IconSlot>
              <IconMagnifyingGlass color={GreyGrey60} />
            </IconSlot>
            <Command.Input
              autoFocus
              value={query}
              onValueChange={setQuery}
              placeholder={SEARCH_PLACEHOLDER}
              data-testid="global-search.input"
            />
            {anyLoading && (
              <IconSlot>
                <Spinner boxSize="14px" color={GreyGrey60} />
              </IconSlot>
            )}
          </Flex>

          <Box h="289px">
            {showEmptyState ? (
              <EmptyState
                variant="subtle"
                icon={() => (
                  <IconMagnifyingGlass size={20} color={GreyGrey60} />
                )}
                iconSize="sm"
                title={emptyStateText}
                data-testid={
                  showNoResults ? 'global-search.no-results' : undefined
                }
              />
            ) : (
              hasResults && (
                <Flex h="100%" flexDir="column">
                  <Command.List>
                    <CustomerResults
                      customers={results.customers}
                      isActive={results.isActive}
                      onNavigate={onNavigate}
                    />
                    <InvoiceResults
                      invoices={results.invoices}
                      isActive={results.isActive}
                      onNavigate={onNavigate}
                    />
                  </Command.List>
                </Flex>
              )
            )}
          </Box>
        </Command>
      </Box>
    </Modal>
  )
}

const cmdkStyles = {
  '[cmdk-root]': {
    width: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  '[cmdk-input-wrapper]': {
    flex: '1 0 0',
    display: 'flex',
    alignItems: 'center',
    minWidth: 0
  },
  '[cmdk-input]': {
    width: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    color: GreyGrey80,
    fontSize: '13px',
    letterSpacing: '-0.08px'
  },
  '[cmdk-input]::placeholder': {
    color: GreyGrey60
  },
  '[cmdk-list]': {
    flex: '1',
    overflowY: 'auto',
    height: '100%',
    padding: '4px'
  },
  '[cmdk-group]': {
    padding: 0
  },
  '[cmdk-group-heading]': {
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    padding: '0 8px',
    color: GreyGrey60,
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0px'
  },
  '[cmdk-item]': {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px',
    height: '40px',
    borderRadius: '4px',
    cursor: 'pointer',
    color: GreyGrey80,
    '&[aria-selected="true"]': {
      background: GreyGrey20
    }
  }
}
